import { useDispatch } from 'react-redux';
import { WidgetID } from '../../../models/widget.model';
import { GridColumnReorderEvent } from '@progress/kendo-react-grid';
import { useCallback, useMemo } from 'react';
import {
  GridConfig,
  isNamedColumnOrder,
  PersistedColumnOrder,
} from '../../../models/grid-config.model';
import { usePersistGrid } from './usePersistUserGrid';
import { setGridConfig } from '../../../store/slices/grid-config.slice';
import { truncateString } from 'common/hooks/utils';
import { ColumnModel } from 'common/models/widget-config.model';

export function useGridWithColumnReordering(id: WidgetID, columns: ColumnModel[]) {
  const dispatch = useDispatch();
  const { gridConfig, storeConfig } = usePersistGrid(id);

  const gridColumns = useMemo(() => {
    const columnOrder = gridConfig?.columnOrder;

    // If we have named column order, sort columns by their position in the saved order
    if (isNamedColumnOrder(columnOrder)) {
      return columns
        .map((col) => {
          const savedIndex = columnOrder.findIndex((saved) => saved.id === col.field);
          return {
            ...col,
            orderIndex: savedIndex >= 0 ? savedIndex : columns.length + columns.indexOf(col),
            title: truncateString(col.title ?? '', 25),
            tooltip: col.title,
          };
        })
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((col, index) => ({ ...col, orderIndex: index }));
    }

    // Legacy numeric format - backward compatibility
    if (columnOrder) {
      return columns.map((col, index) => ({
        ...col,
        orderIndex: columnOrder[index] ?? index,
        title: truncateString(col.title ?? '', 25),
        tooltip: col.title,
      }));
    }

    // No saved order - use default
    return columns.map((col, index) => ({
      ...col,
      orderIndex: index,
      title: truncateString(col.title ?? '', 25),
      tooltip: col.title,
    }));
  }, [columns, gridConfig]);

  const onColumnReorder = useCallback(
    async (e: GridColumnReorderEvent) => {
      // Save as named column order using field as identifier
      const namedOrder: PersistedColumnOrder[] = e.columns
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
        .map((col) => ({
          id: col.field ?? col.title ?? '',
        }));

      if (namedOrder.length > 0) {
        const updatedData: GridConfig = {
          ...gridConfig,
          columnOrder: namedOrder,
        };
        const nextConfig = {
          id: id,
          data: updatedData,
        };
        dispatch(setGridConfig(nextConfig));
        await storeConfig({
          [id]: updatedData,
        });
      }
    },
    [dispatch, id, gridConfig, storeConfig]
  );
  return { gridColumns, onColumnReorder };
}
